/**
 * Lifecycle bucket of a booking (a placement row on a campaign), derived from
 * its live and end dates against today's calendar day. Nothing is stored:
 * a row moves between buckets on its own as the calendar advances.
 */
export const bookingLifecycleBucket = {
  upcoming: "upcoming",
  live: "live",
  completed: "completed",
  undated: "undated",
} as const;

export type BookingLifecycleBucket =
  (typeof bookingLifecycleBucket)[keyof typeof bookingLifecycleBucket];

/** Buckets a user can toggle. Undated rows are always shown. */
export const toggleableBookingLifecycleBuckets = [
  bookingLifecycleBucket.upcoming,
  bookingLifecycleBucket.live,
  bookingLifecycleBucket.completed,
] as const;

export type ToggleableBookingLifecycleBucket =
  (typeof toggleableBookingLifecycleBuckets)[number];

export const bookingLifecycleBucketLabels: Record<
  ToggleableBookingLifecycleBucket,
  string
> = {
  upcoming: "Upcoming",
  live: "Live",
  completed: "Completed",
};

/** Default selection when a campaign is first opened: every bucket shown. */
export const defaultBookingLifecycleSelection: ToggleableBookingLifecycleBucket[] =
  [...toggleableBookingLifecycleBuckets];

export const isToggleableBookingLifecycleBucket = (
  value: unknown
): value is ToggleableBookingLifecycleBucket =>
  typeof value === "string" &&
  (toggleableBookingLifecycleBuckets as readonly string[]).includes(value);
